import { useEffect, useState } from 'react';
import { Target, X } from 'lucide-react';
import { clsx } from 'clsx';
import {
  creativeWorkbenchApi,
  type WorkbenchSteeringCell,
  type WorkbenchSteeringCellResponse,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import type { ChatSeedInput } from './EmbeddedChatPanel';

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

interface Props {
  cell: WorkbenchSteeringCell;
  onClose: () => void;
  onDrillDown: (seed: ChatSeedInput) => void;
}

export function SteeringCellInspector({ cell, onClose, onDrillDown }: Props) {
  const { clientUid } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchSteeringCellResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getSteeringCell({
        clientUid,
        targetPersona: cell.target_persona,
        actualPersona: cell.actual_persona,
      })
      .then((p) => {
        if (!cancelled) setData(p);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid, cell]);

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
            <Target
              size={14}
              className={cell.is_on_target ? 'text-emerald-500' : 'text-amber-500'}
            />
            {cell.is_on_target
              ? `On-target: ${cell.target_persona}`
              : `Off-target: targeted ${cell.target_persona}, attracted ${cell.actual_persona}`}
          </h2>
          <p className="text-[10px] text-slate-400 mt-0.5">
            {CURRENCY.format(cell.spend)} across {cell.ad_count} ads —{' '}
            {cell.row_share_pct.toFixed(1)}% of {cell.target_persona} target spend
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-700"
          aria-label="Close"
        >
          <X size={14} />
        </button>
      </header>

      {loading && !data && <div className="text-xs text-slate-400">Loading...</div>}
      {data && data.ads.length === 0 && (
        <div className="text-xs text-slate-400">No ads in this cell.</div>
      )}
      {data && data.ads.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-100">
                <th className="font-mono uppercase pr-2 py-1">Ad</th>
                <th className="font-mono uppercase px-2 py-1">Formula</th>
                <th className="font-mono uppercase px-2 py-1 text-right">Spend</th>
                <th className="font-mono uppercase px-2 py-1 text-right">LTV ROAS</th>
                <th className="font-mono uppercase px-2 py-1 text-right">CTR</th>
              </tr>
            </thead>
            <tbody>
              {data.ads.map((ad) => (
                <tr
                  key={ad.ad_id}
                  className="border-b border-slate-50 hover:bg-slate-50"
                >
                  <td className="pr-2 py-2 max-w-xs">
                    <div
                      className="font-medium text-navy-900 truncate"
                      title={ad.ad_name ?? ad.ad_id}
                    >
                      {ad.ad_name ?? ad.ad_id}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-slate-500 font-mono text-[10px]">
                    {ad.hook_type} · {ad.tone}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-navy-900">
                    {CURRENCY.format(ad.spend)}
                  </td>
                  <td
                    className={clsx(
                      'px-2 py-2 text-right font-mono font-bold',
                      ad.ltv_roas >= 1 ? 'text-teal-600' : 'text-red-600',
                    )}
                  >
                    {ad.ltv_roas.toFixed(2)}x
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-slate-500">
                    {(ad.ctr * 100).toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <footer className="flex justify-end pt-2 border-t border-slate-100">
        <button
          onClick={() =>
            onDrillDown({
              question: cell.is_on_target
                ? `What's working in ads we successfully steered to ${cell.target_persona}?`
                : `Why are ads we targeted at ${cell.target_persona} actually attracting ${cell.actual_persona}?`,
              chart_axis: {
                series: 'steering_cell',
                target_persona: cell.target_persona,
                actual_persona: cell.actual_persona,
              },
            })
          }
          className="text-[10px] font-mono text-teal-600 hover:text-teal-700"
        >
          Ask the analyst →
        </button>
      </footer>
    </section>
  );
}
